import { clubApi } from '@repo/api-client';
import { urlPublicTournament } from '@/urls';
import { LoaderFunctionArgs, Outlet, redirect, useLoaderData } from 'react-router';
import { route as openInscriptionsRoute } from './open-inscriptions/layout';
import { route as pendingRoute } from './pending';
import { route as finishedRoute } from './finished';
import { route as activeRoute } from './active';
import { DateFormat, formatDate } from '@/data/format/format-date';
import { useEffect } from 'react';

const PublicTournamentIndex = () => {
  const { tournament } = useLoaderData<typeof loader>();

  const dateDescription = `${formatDate(tournament.startDate, DateFormat.DD_MM)} - ${formatDate(
    tournament.endDate,
    DateFormat.DD_MM
  )}`;

  useEffect(() => {
    document.title = `Club: ${tournament.club.name} - Torneo: ${tournament.name}`;
    document
      .querySelector('meta[property="og:title"]')
      ?.setAttribute('content', `Club: ${tournament.club.name} - Torneo: ${tournament.name}`);
    document
      .querySelector('meta[property="og:description"]')
      ?.setAttribute('content', `Fecha: ${dateDescription}`);
  }, [tournament]);

  return (
    <div className="flex flex-col h-full">
      <div className="bg-primary text-white p-4">
        <div className="flex items-center gap-4 max-w-lg mx-auto">
          <div className="bg-white rounded-full p-2">
            <img src={tournament.club.logo} alt={tournament.club.name} className="w-10 h-10" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">{tournament.name}</h1>
            <h2>{dateDescription}</h2>
          </div>
        </div>
      </div>
      <div className="flex-1 flex flex-col space-y-4 max-w-lg mx-auto w-full p-4">
        <Outlet />
      </div>
    </div>
  );
};

const tournamentStatusToUrl = (status: Tournament['status']) => {
  return status.toLowerCase().replace('_', '-');
};

const loader = async ({ params, request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const segments = url.pathname.split('/').filter(Boolean);
  // Look at the segment right after the tournamentId — that's the status,
  // regardless of any nested child routes that follow it.
  const tournamentIdIndex = segments.indexOf(String(params.tournamentId));
  const statusFromUrl =
    tournamentIdIndex >= 0 && tournamentIdIndex + 1 < segments.length
      ? segments[tournamentIdIndex + 1]
      : null;
  const isAtTournamentRoot = !statusFromUrl;
  const tournamentId = params.tournamentId as string;
  const tournament = await clubApi.getPublicTournamentById(tournamentId);
  if (
    isAtTournamentRoot ||
    tournamentStatusToUrl(tournament.status) !== statusFromUrl?.toLowerCase()
  ) {
    const url = urlPublicTournament(
      tournamentId.toString(),
      tournamentStatusToUrl(tournament.status)
    );
    return redirect(url);
  }
  return { tournament };
};

export const idPublicTournamentIndex = 'publicTournamentIndex';

export const route = {
  id: idPublicTournamentIndex,
  loader,
  element: <PublicTournamentIndex />,
  children: [
    openInscriptionsRoute,
    activeRoute,
    pendingRoute,
    finishedRoute,
    {
      path: ':status',
      loader: ({ params }: LoaderFunctionArgs) =>
        redirect(urlPublicTournament(params.id as string, params.tournamentId as string)),
    },
  ],
  path: urlPublicTournament(':tournamentId'),
};
